import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["countrySelect", "postalCodeInput", "nipInput", "nipError"]

  static NIP_WEIGHTS = [6, 5, 7, 2, 3, 4, 5, 6, 7]
  static NIP_FORMAT = /^(\d{3}-\d{3}-\d{2}-\d{2}|\d{3}-\d{2}-\d{2}-\d{3}|\d{10})$/

  connect() {
    this.updatePostalCodeKeyboard()
    // Select kraju dostawy jest podmieniany przy przeliczeniu koszyka
    // (refresh.js), wiec nasluch wisi na calym bloku, nie na selekcie.
    this.onCountryChange = (event) => {
      if (event.target.matches("[data-delivery-address-target~='countrySelect']")) this.updatePostalCodeKeyboard()
    }
    this.element.addEventListener("change", this.onCountryChange)
  }

  disconnect() {
    this.element.removeEventListener("change", this.onCountryChange)
  }

  // Polski kod to same cyfry, myslnik wstawia formatPostalCode - na telefonie
  // klawiatura numeryczna. Kody zagraniczne bywaja z literami (np. SW1A 1AA).
  updatePostalCodeKeyboard() {
    const mode = this.isCountryPoland() ? "numeric" : "text"
    this.postalCodeInputTargets.forEach((input) => { input.inputMode = mode })
  }

  validateNip() {
    if (!this.hasNipInputTarget) return

    const raw = this.nipInputTarget.value.replace(/\s+/g, "")
    const errorEl = this.hasNipErrorTarget ? this.nipErrorTarget : null

    if (raw.length === 0) {
      this.clearNipError(errorEl)
      return
    }

    if (!this.isCountryPoland()) {
      this.clearNipError(errorEl)
      return
    }

    if (!this.constructor.NIP_FORMAT.test(raw)) {
      this.showNipError(errorEl, "Niepoprawny format NIP")
      return
    }

    const digits = raw.replace(/\D/g, "")
    const sum = this.constructor.NIP_WEIGHTS.reduce((acc, weight, i) => acc + weight * parseInt(digits[i], 10), 0)
    if (sum % 11 !== parseInt(digits[9], 10)) {
      this.showNipError(errorEl, "Niepoprawny numer NIP")
    } else {
      this.clearNipError(errorEl)
    }
  }

  countryChanged() {
    this.validateNip()
  }

  // While the user is still typing, clear any visible error so a stale
  // "Niepoprawny format NIP" does not linger as they fix the number. The
  // authoritative check runs on blur (validateNip). Also strip whitespace as it
  // is typed/pasted ("123 456 32 18") so the field never holds spaces the NIP
  // format rejects - mirrors how the GUS fetch normalizes the number.
  onNipInput() {
    if (this.hasNipInputTarget) {
      const stripped = this.nipInputTarget.value.replace(/\s+/g, "")
      if (stripped !== this.nipInputTarget.value) {
        this.nipInputTarget.value = stripped
      }
    }
    if (this.hasNipErrorTarget) {
      this.clearNipError(this.nipErrorTarget)
    }
  }

  showNipError(errorEl, message) {
    if (!errorEl) return
    errorEl.textContent = message
    errorEl.style.display = "block"
    this.nipInputTarget.classList.add("is-invalid")
  }

  clearNipError(errorEl) {
    if (errorEl) {
      errorEl.textContent = ""
      errorEl.style.display = "none"
    }
    if (this.hasNipInputTarget) {
      this.nipInputTarget.classList.remove("is-invalid")
    }
  }

  formatPostalCode(event) {
    if (!this.isCountryPoland() || event.inputType === "deleteContentBackward") return;
    const el = this.postalCodeInputTarget
    let value = el.value.replace(/[^\d]/g, "")

    // iOS keyboard suggestions append the picked code to digits already typed
    // ("2" + "26-605" -> "22-660"). A full code inserted at once replaces the field.
    const inserted = (event.data ?? event.dataTransfer?.getData("text/plain") ?? "").replace(/[^\d]/g, "")
    if (inserted.length === 5) value = inserted

    if (value.length > 1) {
      value = value.slice(0, 2) + "-" + value.slice(2, 5)
    }

    el.value = value
  }

  getCountry() {
    if (!this.hasCountrySelectTarget) { return null; }
    // Pusty select (kraj jeszcze niewybrany) to "", nie null - inaczej
    // isCountryPoland() formatowal kod pocztowy po polsku przed wyborem kraju.
    return this.countrySelectTarget.value;
  }

  isCountryPoland() {
    const country = this.getCountry();
    if (country === "Polska" || country === null || country === undefined) {
      return true;
    }
    return false;
  }
}
